import { axiosInstance, type CustomResponseFormat } from "api/instance";
import { normalizeTaskEditorState } from "pages/task-editor/utils/taskEditorRuntime";
import type { TaskEditorState } from "pages/task-editor/utils/taskEditorUtils";

const TASK_EDITOR_DOMAIN = "task-editor";

const TASK_EDITOR_LIST_ENDPOINT = "task/list";
const TASK_EDITOR_SAVE_ENDPOINT = "task/save";
const TASK_EDITOR_REMOVE_ENDPOINT = "task/remove";
const TASK_EDITOR_AUTH_HEADER_KEY = "X-Auth-Token";
const TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR =
  "taskEditorApi requires user access token.";

// 서버 응답 envelope.
//   /task/list → TaskListResponseItem[]
// envelope 의 taskId 는 camelCase. 내부 task 는 snake_case TaskEditorState.
interface TaskListResponseItem {
  taskId: string;
  task: TaskEditorState;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function unwrapResponsePayload<T>(
  response: CustomResponseFormat<T> | undefined
): unknown {
  if (!response) {
    return undefined;
  }

  const result = response.result;
  if (isRecord(result) && "data" in result) {
    return (result as Record<string, unknown>).data;
  }
  if (result !== undefined) {
    return result;
  }
  return response.data;
}

function isTaskListResponseItem(value: unknown): value is TaskListResponseItem {
  if (!isRecord(value)) return false;
  return typeof value.taskId === "string" && isRecord(value.task);
}

function normalizeTaskListResponse(
  payload: unknown
): Record<string, TaskEditorState> {
  if (!Array.isArray(payload)) return {};

  return payload.reduce<Record<string, TaskEditorState>>((acc, item) => {
    if (!isTaskListResponseItem(item)) return acc;

    const taskId = item.taskId.trim();
    if (!taskId) return acc;

    const normalized = normalizeTaskEditorState({
      ...item.task,
      task_id: taskId,
    });

    return { ...acc, [taskId]: normalized };
  }, {});
}

function getTaskEditorAccessTokenFromStorage() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return undefined;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    if (
      isRecord(parsedUser) &&
      typeof parsedUser.accessToken === "string" &&
      parsedUser.accessToken.trim().length > 0
    ) {
      return parsedUser.accessToken;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function getTaskEditorAccessTokenFromAxiosDefaults() {
  const headerToken =
    axiosInstance.defaults.headers?.[TASK_EDITOR_AUTH_HEADER_KEY];

  return typeof headerToken === "string" && headerToken.trim().length > 0
    ? headerToken
    : undefined;
}

function requireTaskEditorAccessToken() {
  const accessToken =
    getTaskEditorAccessTokenFromStorage() ??
    getTaskEditorAccessTokenFromAxiosDefaults();

  if (!accessToken) {
    throw new Error(TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR);
  }

  return accessToken;
}

function getTaskEditorAuthConfig(config?: {
  params?: Record<string, unknown>;
  headers?: Record<string, unknown>;
}) {
  const accessToken = requireTaskEditorAccessToken();

  return {
    ...config,
    headers: {
      ...(config?.headers ?? {}),
      [TASK_EDITOR_AUTH_HEADER_KEY]: accessToken,
    },
  };
}

export const taskEditorApi = {
  getTasksByTaskId: async (): Promise<Record<string, TaskEditorState>> => {
    const response = await axiosInstance.get<
      CustomResponseFormat<TaskListResponseItem[]>
    >(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_LIST_ENDPOINT}`,
      getTaskEditorAuthConfig()
    );

    return normalizeTaskListResponse(unwrapResponsePayload(response));
  },

  saveTask: async (task_id: string, task: TaskEditorState) => {
    if (!task_id) return;
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_SAVE_ENDPOINT}`,
      { task: { ...task, task_id } },
      getTaskEditorAuthConfig({
        params: { task_id },
      })
    );
  },

  removeTask: async (task_id: string) => {
    if (!task_id) return;
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_REMOVE_ENDPOINT}`,
      undefined,
      getTaskEditorAuthConfig({
        params: { task_id },
      })
    );
  },
};
